import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  subMonths,
  subQuarters,
  subYears
} from 'date-fns';
import debounce from 'lodash/debounce';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';

const GlobalFilter = ({
  onFilterChange,
  initialFilters = {},
  showDateFilters = true,
  showSearch = true,
  showCategoryFilter = false,
  showStatusFilter = false,
  categories = [],
  statuses = [],
  placeholder = 'Search...',
  className = '',
  compact = true, // Default to compact
  defaultToCurrentFY = true // Default to current financial year
}) => {
  const { t } = useLanguage();

  const [filters, setFilters] = useState({
    search: '',
    dateRange: defaultToCurrentFY ? 'currentFY' : 'all',
    customStartDate: null,
    customEndDate: null,
    category: '',
    status: '',
    ...initialFilters
  });

  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(!compact);

  // GST Financial Year periods
  const getFinancialYearPeriods = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Financial year: April to March
    const fyStart = currentMonth >= 3 ? currentYear : currentYear - 1;
    const fyEnd = fyStart + 1;

    return {
      currentFY: {
        start: new Date(fyStart, 3, 1), // April 1
        end: new Date(fyEnd, 2, 31) // March 31
      },
      previousFY: {
        start: new Date(fyStart - 1, 3, 1),
        end: new Date(fyEnd - 1, 2, 31)
      }
    };
  };

  // Date range options
  const dateRangeOptions = [
    { value: 'all', label: 'All Time', icon: '📅' },
    { value: 'today', label: 'Today', icon: '📆' },
    { value: 'yesterday', label: 'Yesterday', icon: '📅' },
    { value: 'thisWeek', label: 'This Week', icon: '📊' },
    { value: 'lastWeek', label: 'Last Week', icon: '📈' },
    { value: 'thisMonth', label: 'This Month', icon: '🗓️' },
    { value: 'lastMonth', label: 'Last Month', icon: '📅' },
    { value: 'thisQuarter', label: 'This Quarter', icon: '📊' },
    { value: 'lastQuarter', label: 'Last Quarter', icon: '📈' },
    { value: 'thisYear', label: 'This Year', icon: '🎯' },
    { value: 'lastYear', label: 'Last Year', icon: '🏆' },
    { value: 'currentFY', label: 'Current FY (GST)', icon: '💼' },
    { value: 'previousFY', label: 'Previous FY (GST)', icon: '📋' },
    { value: 'custom', label: 'Custom Range', icon: '🔧' }
  ];

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      const updatedFilters = { ...filters, search: searchValue };
      setFilters(updatedFilters);
      onFilterChange(updatedFilters);
    }, 300),
    [filters, onFilterChange]
  );

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value }));
    debouncedSearch(value);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters, [key]: value };

    // Reset custom dates when changing date range
    if (key === 'dateRange' && value !== 'custom') {
      updatedFilters.customStartDate = null;
      updatedFilters.customEndDate = null;
      setShowCustomDatePicker(false);
    }

    // Show custom date picker
    if (key === 'dateRange' && value === 'custom') {
      setShowCustomDatePicker(true);
    }

    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  // Get date range from selection
  const getDateRange = (rangeType) => {
    const now = new Date();
    const fy = getFinancialYearPeriods();

    switch (rangeType) {
      case 'today':
        return { start: startOfDay(now), end: endOfDay(now) };
      case 'yesterday':
        const yesterday = subDays(now, 1);
        return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
      case 'thisWeek':
        return { start: startOfWeek(now), end: endOfWeek(now) };
      case 'lastWeek':
        const lastWeek = subWeeks(now, 1);
        return { start: startOfWeek(lastWeek), end: endOfWeek(lastWeek) };
      case 'thisMonth':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'lastMonth':
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'thisQuarter':
        return { start: startOfQuarter(now), end: endOfQuarter(now) };
      case 'lastQuarter':
        const lastQuarter = subQuarters(now, 1);
        return { start: startOfQuarter(lastQuarter), end: endOfQuarter(lastQuarter) };
      case 'thisYear':
        return { start: startOfYear(now), end: endOfYear(now) };
      case 'lastYear':
        const lastYear = subYears(now, 1);
        return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
      case 'currentFY':
        return fy.currentFY;
      case 'previousFY':
        return fy.previousFY;
      case 'custom':
        return {
          start: filters.customStartDate,
          end: filters.customEndDate
        };
      default:
        return null;
    }
  };

  // Clear all filters
  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      dateRange: 'all',
      customStartDate: null,
      customEndDate: null,
      category: '',
      status: ''
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
    setShowCustomDatePicker(false);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.search ||
           filters.dateRange !== 'all' ||
           filters.category ||
           filters.status ||
           filters.customStartDate ||
           filters.customEndDate;
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.dateRange !== 'all') count++;
    if (filters.category) count++;
    if (filters.status) count++;
    if (filters.customStartDate || filters.customEndDate) count++;
    return count;
  };

  // Load saved filters from localStorage
  useEffect(() => {
    const savedFilters = localStorage.getItem('acctoo_filters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.warn('Failed to load saved filters:', error);
      }
    }
  }, []);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem('acctoo_filters', JSON.stringify(filters));
  }, [filters]);

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
      {/* Compact Header */}
      <div className="px-3 py-2 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filters</span>
            {hasActiveFilters() && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                {getActiveFilterCount()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters() && (
              <button
                onClick={clearFilters}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
            {compact && (
              <button
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg
                  className={`w-4 h-4 transform transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Content */}
      {(!compact || isFiltersOpen) && (
        <div className="p-3 space-y-3">
          {/* Search */}
          {showSearch && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.search}
                  onChange={handleSearchChange}
                  placeholder={placeholder}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {filters.search && (
                  <button
                    onClick={() => handleFilterChange('search', '')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Date Filters */}
          {showDateFilters && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <AppSelect
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                aria-label="Date range"
                sheetTitle="Date range"
                options={dateRangeOptions.map((option) => ({
                  value: option.value,
                  label: `${option.icon} ${option.label}`,
                }))}
                triggerClassName={APP_SELECT_FILTER_TRIGGER}
              />
            </div>
          )}

          {/* Custom Date Picker */}
          {showCustomDatePicker && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.customStartDate || ''}
                  onChange={(e) => handleFilterChange('customStartDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.customEndDate || ''}
                  onChange={(e) => handleFilterChange('customEndDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Category Filter */}
          {showCategoryFilter && categories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <AppSelect
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                placeholder="All Categories"
                aria-label="Category"
                sheetTitle="Category"
                options={[
                  { value: '', label: 'All Categories' },
                  ...categories.map((category) => ({ value: category.value, label: category.label })),
                ]}
                triggerClassName={APP_SELECT_FILTER_TRIGGER}
              />
            </div>
          )}

          {/* Status Filter */}
          {showStatusFilter && statuses.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <AppSelect
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                placeholder="All Status"
                aria-label="Status"
                sheetTitle="Status"
                options={[
                  { value: '', label: 'All Status' },
                  ...statuses.map((status) => ({ value: status.value, label: status.label })),
                ]}
                triggerClassName={APP_SELECT_FILTER_TRIGGER}
              />
            </div>
          )}
        </div>
      )}

      {/* Current Filters Display */}
      {hasActiveFilters() && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {filters.search && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                Search: "{filters.search}"
                <button
                  onClick={() => handleFilterChange('search', '')}
                  className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}

            {filters.dateRange !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                Date: {dateRangeOptions.find(opt => opt.value === filters.dateRange)?.label}
                <button
                  onClick={() => handleFilterChange('dateRange', 'all')}
                  className="ml-1 hover:bg-green-200 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}

            {filters.category && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                Category: {categories.find(cat => cat.value === filters.category)?.label}
                <button
                  onClick={() => handleFilterChange('category', '')}
                  className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}

            {filters.status && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                Status: {statuses.find(stat => stat.value === filters.status)?.label}
                <button
                  onClick={() => handleFilterChange('status', '')}
                  className="ml-1 hover:bg-orange-200 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalFilter;
